/* eslint-disable no-case-declarations */
/**
 * Standalone Popups JavaScript
 *
 * This is a self-contained popup system that doesn't rely on the main theme's
 * module loading system for faster initialization and better performance.
 *
 * @package ppd
 * @since   1.0.0
 */

import {
	animate,
	disableScroll,
	enableScroll,
	setCookie,
	getCookie,
	deleteCookiesWithPrefix,
} from './theme/helpers';
import { hasFunctionalConsent } from './theme/consent';

// Script loading functionality for popup content
const loadScriptsFromElement = async (element, popupId) => {
	console.log(`🚀 Starting script loading for popup: ${popupId}`);

	// Find all script tags within the element
	const scripts = element.querySelectorAll('script');

	console.log(`📜 Found ${scripts.length} script tags in popup: ${popupId}`);

	if (scripts.length === 0) {
		console.log(`✅ No scripts to load for popup: ${popupId}`);
		return; // No scripts to load
	}

	// Separate external and inline scripts
	const externalScripts = [];
	const inlineScripts = [];

	scripts.forEach((script) => {
		if (script.src && !script.hasAttribute('data-loaded')) {
			externalScripts.push(script);
		} else if (!script.src && !script.hasAttribute('data-executed')) {
			inlineScripts.push(script);
		}
	});

	console.log(`📦 External scripts to load: ${externalScripts.length}`);
	console.log(`📝 Inline scripts to execute: ${inlineScripts.length}`);

	// Load all external scripts first
	const scriptPromises = externalScripts.map((script, index) => new Promise((resolve, reject) => {
		console.log(`⬇️ Loading external script ${index + 1}/${externalScripts.length}: ${script.src}`);

		// Check if script is already loaded globally
		const existingScript = document.querySelector(`script[src="${script.src}"]`);
		if (existingScript && existingScript.hasAttribute('data-loaded')) {
			console.log(`♻️ Script already loaded globally: ${script.src}`);
			script.setAttribute('data-loaded', 'true');
			resolve();
			return;
		}

		const newScript = document.createElement('script');
		newScript.src = script.src;

		// Copy relevant attributes
		if (script.type) newScript.type = script.type;
		if (script.async !== undefined) newScript.async = script.async;
		if (script.defer !== undefined) newScript.defer = script.defer;

		newScript.onload = () => {
			console.log(`✅ Successfully loaded external script: ${script.src}`);
			script.setAttribute('data-loaded', 'true');
			newScript.setAttribute('data-loaded', 'true');
			resolve();
		};

		newScript.onerror = () => {
			console.error(`❌ Error loading popup script: ${script.src}`);
			reject(new Error(`Failed to load: ${script.src}`));
		};

		document.head.appendChild(newScript);
	}));

	try {
		// Wait for all external scripts to be loaded
		await Promise.all(scriptPromises);
		console.log(`✅ All external scripts loaded for popup: ${popupId}`);
	} catch (error) {
		console.error(`❌ Error loading external scripts for popup: ${popupId}`, error);
	}

	// Execute inline scripts after external scripts have loaded
	console.log(`⚡ Executing ${inlineScripts.length} inline scripts for popup: ${popupId}`);
	inlineScripts.forEach((script, index) => {
		try {
			console.log(`⚡ Executing inline script ${index + 1}/${inlineScripts.length}`);

			// Create a new script element to execute the inline code
			const newScript = document.createElement('script');
			newScript.textContent = script.textContent;

			// Copy type if present
			if (script.type) newScript.type = script.type;

			// Execute the script
			document.head.appendChild(newScript);

			// Mark original as executed
			script.setAttribute('data-executed', 'true');

			// Remove the executed script to clean up
			document.head.removeChild(newScript);

			console.log(`✅ Successfully executed inline script ${index + 1}`);
		} catch (error) {
			console.error(`❌ Error executing inline script ${index + 1}:`, error);
		}
	});

	console.log(`🎉 Script loading completed for popup: ${popupId}`);
};

// Focus trap implementation for standalone popups
const focusableQuerySelector = 'a[href], area[href], input:not([disabled]):not([type="hidden"]):not([aria-hidden]), select:not([disabled]):not([aria-hidden]), textarea:not([disabled]):not([aria-hidden]), button:not([disabled]):not([aria-hidden]), iframe, object, embed, [tabindex="0"], [contenteditable], [draggable=true]';

function disableFocusTrap() {
	if (!window?.emPopupSystem?.focusTrap?.el || !window?.emPopupSystem?.focusTrap?.callback) {
		return;
	}

	const { el, callback } = window.emPopupSystem.focusTrap;
	if (el && callback) {
		el.removeEventListener('keydown', callback);
	}
}

function enableFocusTrap(el, setTabIndex = false) {
	disableFocusTrap();
	if (setTabIndex) {
		el.setAttribute('tabindex', '0');
	}

	const getFocusEls = () => {
		const allFocusEls = el.querySelectorAll(focusableQuerySelector);

		// Iterate through all focusable elements and remove the ones that are hidden.
		const focusEls = Array.from(allFocusEls).filter((e) => {
			// Check if any ancestor has display: none
			let ancestor = e.parentElement;

			while (ancestor) {
				const styles = getComputedStyle(ancestor);
				if (styles.display === 'none' || styles.visibility === 'hidden') {
					return false;
				}
				ancestor = ancestor.parentElement;
			}

			const styles = getComputedStyle(e);
			return styles.display !== 'none' && styles.visibility !== 'hidden';
		});

		return focusEls;
	};

	const handleTabNavigation = (event) => {
		const { key, shiftKey } = event;
		if (key !== 'Tab') return;

		const focusEls = getFocusEls();
		if (focusEls.length === 0) return;

		const firstFocusEl = focusEls[0];
		const lastFocusEl = focusEls[focusEls.length - 1];

		if (shiftKey && document.activeElement === firstFocusEl) {
			lastFocusEl.focus();
			event.preventDefault();
		} else if (!shiftKey && document.activeElement === lastFocusEl) {
			firstFocusEl.focus();
			event.preventDefault();
		}
	};

	el.addEventListener('keydown', handleTabNavigation);

	// Store for cleanup using popup system namespace
	if (!window.emPopupSystem) {
		window.emPopupSystem = {};
	}
	window.emPopupSystem.focusTrap = { el, callback: handleTabNavigation };

	// Focus the first focusable element
	const focusEls = getFocusEls();
	if (focusEls.length > 0) {
		focusEls[0].focus();
	}
}

// Popup Class (modified to remove embark dependencies)

class Popup {
	hasBeenInitialized = false;

	scriptsLoaded = false;

	constructor(el) {
		if (el) {
			this.el = el;
			this.cookieName = `em_popup_${el.id}`;
			this.cookieDuration = el.dataset.expiration;

			if (this.getCookie()) {
				this.destroy();
			} else {
				this.init();
			}
		}
	}

	init = () => {
		const { triggers, enterAnimation, type } = this.el.dataset;

		this.overlayEl = this.el.querySelector('.em-popup__overlay');
		this.contentEl = this.el.querySelector('.em-popup__content');
		this.closeEls = this.el.querySelectorAll('.em-popup__close');

		this.type = type;
		this.triggers = JSON.parse(triggers);
		this.enterAnimation = enterAnimation;

		this.timeTriggers = [];
		this.scrollTriggers = [];
		this.exitTriggers = [];
		this.linkUrlsTriggers = [];
		this.clickedLink = null;

		this.closeEls.forEach((closeEl) => {
			closeEl.addEventListener('click', this.hide);
		});

		switch (this.type) {
			case 'modal':
				document.addEventListener('keyup', this.handleKeyup);
				this.overlayEl.addEventListener('click', this.hide);
				break;
			default:
				break;
		}

		this.triggers.forEach((settings) => {
			const { trigger } = settings;
			switch (trigger) {
				case 'time':
					const timer = setTimeout(() => {
						this.show();
					}, settings.time);
					this.timeTriggers.push(timer);
					break;

				case 'scroll':
					const scrollHandler = () => {
						if (this.canBeShown()) {
							const totalHeight = document.body.scrollHeight - window.innerHeight;
							const scrollPercentage = (window.scrollY / totalHeight) * 100;

							if (scrollPercentage >= settings.scroll_amount) {
								this.show();
								window.removeEventListener('scroll', scrollHandler);
							}
						}
					};
					window.addEventListener('scroll', scrollHandler);
					this.scrollTriggers.push(scrollHandler);
					break;

				case 'exit':
					const exitHandler = () => {
						if (this.canBeShown()) {
							this.show();
							document.documentElement.removeEventListener('mouseleave', exitHandler);
						}
					};
					document.documentElement.addEventListener('mouseleave', exitHandler);
					this.exitTriggers.push(exitHandler);
					break;

				case 'link_urls':
					if (settings.link_urls) {
						const urls = settings.link_urls.split('\n').filter((url) => url.trim() !== '');

						if (urls.length) {
							const linkClickHandler = (e) => {
								// Check if the click is on an anchor element or a child of an anchor
								const targetElement = e.target;
								const anchor = targetElement.closest('a');

								// If we found an anchor and it has an href
								if (anchor && anchor.href) {
									// Check if the href contains any of our target URLs
									const matchingUrl = urls.find((url) => {
										const trimmedUrl = url.trim();
										return trimmedUrl && anchor.href.includes(trimmedUrl);
									});

									if (matchingUrl) {
										e.preventDefault();
										e.stopImmediatePropagation();
										if (this.canBeShown()) {
											// Store the entire link element
											this.clickedLink = anchor;
											this.show();
										}
									}
								}
							};

							document.addEventListener('click', linkClickHandler, true);
							this.linkUrlsTriggers.push(linkClickHandler);

							// Enable pointer events for this popup's trigger links
							urls.forEach((url) => {
								const trimmedUrl = url.trim();
								if (trimmedUrl) {
									const selector = `[href*="${trimmedUrl}"]`;
									const triggerLinks = document.querySelectorAll(selector);
									triggerLinks.forEach((link) => {
										link.style.pointerEvents = 'all';
									});
								}
							});
						}
					}
					break;

				default:
					break;
			}
		});

		this.hasBeenInitialized = true;
	};

	handleKeyup = (e) => {
		if (e.key === 'Escape') {
			this.hide();
		}
	};

	fixGravityFormsDisplay = () => {
		// Find any Gravity Forms in the popup and remove display:none
		const gravityFormWrappers = this.el.querySelectorAll('.gform_wrapper');

		gravityFormWrappers.forEach((wrapper) => {
			if (wrapper.style.display === 'none') {
				wrapper.style.display = '';
			}
		});
	};

	pauseVideos = () => {
		// Find all video elements in the popup (includes HTML5, Brightcove, etc.)
		const videoElements = this.el.querySelectorAll('video');
		videoElements.forEach((video) => {
			if (!video.paused) {
				video.pause();
			}
		});
	};

	show = async () => {
		if (this.canBeShown()) {
			window.emPopupSystem.openPopup = this;
			const popupId = this.el.id || 'unnamed';

			// Load and execute any scripts within the popup content FIRST (only once)
			if (!this.scriptsLoaded) {
				console.log(`🔄 First time opening popup: ${popupId} - loading scripts`);
				try {
					await loadScriptsFromElement(this.el, popupId);
					this.scriptsLoaded = true;
					console.log(`✅ Scripts loaded for popup: ${popupId}`);
				} catch (error) {
					console.error(`❌ Error loading popup scripts for: ${popupId}`, error);
				}
			} else {
				console.log(`♻️ Scripts already loaded for popup: ${popupId} - skipping script loading`);
			}

			// Now that scripts are loaded, proceed with showing the popup
			console.log(`🎭 Displaying popup: ${popupId}`);
			this.el.setAttribute('aria-hidden', 'false');
			this.el.setAttribute('tabindex', '0');
			this.el.classList.add('show');
			animate(this.overlayEl, 'fadeIn');
			animate(this.contentEl, this.enterAnimation, '0.3s');

			// Fix Gravity Forms display issues
			this.fixGravityFormsDisplay();

			// Update any forwarding links with the clicked link attributes
			if (this.clickedLink) {
				const forwardingLinks = this.el.querySelectorAll('.em-popup-forwarding-link');
				forwardingLinks.forEach((link) => {
					// Set href from the original clicked link
					link.href = this.clickedLink.href;

					// Copy target attribute if it exists
					if (this.clickedLink.hasAttribute('target')) {
						link.setAttribute('target', this.clickedLink.getAttribute('target'));
					} else {
						// Remove target if not present on original link
						link.removeAttribute('target');
					}

					// Copy rel attribute if it exists
					if (this.clickedLink.hasAttribute('rel')) {
						link.setAttribute('rel', this.clickedLink.getAttribute('rel'));
					} else {
						// Remove rel if not present on original link
						link.removeAttribute('rel');
					}
				});
			}

			if (this.type === 'modal') {
				disableScroll();
				enableFocusTrap(this.el);
			}

			console.log(`🎉 Popup displayed successfully: ${popupId}`);
		}
	};

	hide = () => {
		if (this.isVisible()) {
			// Pause any videos in the popup before hiding
			this.pauseVideos();

			let exitAnimation = 'fadeOut';
			if (['side', 'banner'].includes(this.type)) {
				exitAnimation = this.enterAnimation.replace('In', 'Out');
			}

			animate(this.el, exitAnimation).then(() => {
				this.el.classList.remove('show');
				this.overlayEl.style.visibility = 'hidden';
				this.contentEl.style.visibility = 'hidden';

				if (this.type === 'modal') {
					enableScroll();
					disableFocusTrap();
				}

				// Only set cookie if this wasn't triggered by a link click
				// This allows the popup to be shown again when clicking the same link
				const wasLinkTriggered = this.clickedLink !== null;
				if (!wasLinkTriggered) {
					this.setCookie();
				}

				// Reset clicked link reference
				this.clickedLink = null;

				window.emPopupSystem.openPopup = null;
			});
		}
	};

	isVisible = () => this.el.classList.contains('show');

	canBeShown = () => {
		// Always allow showing if triggered by a link click
		// (We'll store the link element after this check passes)
		if (window.emPopupSystem.openPopup || this.isVisible()) {
			return false;
		}

		// Check cookie (unless the popup is about to be triggered by a link)
		return !this.getCookie();
	};

	setCookie = () => {
		// Persist the popup-seen cookie only with functional consent.
		if (hasFunctionalConsent()) {
			setCookie(this.cookieName, 'true', this.cookieDuration);
		}
	};

	getCookie = () => getCookie(this.cookieName);

	destroy = () => {
		if (this.hasBeenInitialized) {
			this.closeEls.forEach((closeEl) => {
				closeEl.removeEventListener('click', this.hide);
			});
			if (this.overlayEl) {
				this.overlayEl.removeEventListener('click', this.hide);
			}
			document.removeEventListener('keyup', this.handleKeyup);

			this.timeTriggers.forEach(clearTimeout);
			this.scrollTriggers.forEach((handler) => window.removeEventListener('scroll', handler));
			this.exitTriggers.forEach((handler) => document.removeEventListener('mouseleave', handler));
			this.linkUrlsTriggers.forEach((handler) => document.removeEventListener('click', handler));

			window.emPopupSystem.instances = window.emPopupSystem.instances.filter(
				(popup) => popup.el !== this.el,
			);
		}

		this.el.remove();
	};
}

// Initialize the global popup system (replaces embark.components.popups)
window.emPopupSystem = {
	// All instances of the Popup class.
	instances: [],
	// The current open popup. Used to avoid opening multiple popups at the same time.
	openPopup: null,
	// Helper function to delete all popup cookies.
	deleteCookies: () => deleteCookiesWithPrefix('em_popup_'),
};

// Initialize popups function
function initPopups() {
	// Get all .em-popup elements.
	const popups = [...document.querySelectorAll('.em-popup')];
	if (popups.length) {
		// Create a Popup instance for each .em-popup element.
		window.emPopupSystem.instances = [
			...window.emPopupSystem.instances,
			...popups.map((popup) => new Popup(popup)),
		];
	}
}

// Create a MutationObserver to watch for new popups.
const observer = new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		if (mutation.addedNodes.length) {
			mutation.addedNodes.forEach((node) => {
				if (node && node.classList && node.classList.contains('em-popups-container')) {
					// Initialize popups.
					initPopups();
					observer.disconnect();
				}
			});
		}
	});
});

// Initialize when DOM is ready
window.addEventListener('emPopupReady', () => {
	// Observe the body for new popups.
	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Initialize popups immediately if they already exist
	initPopups();
});
